// src/modules/cashflow-simulator/api.js
// Uses your configured axios instance at '@/api'
import api from '@/api';

/* --------------------------- helpers --------------------------- */
function normalizeRunPayload(d = {}) {
  return {
    ok: Boolean(d?.ok ?? true),
    runId: d?.runId ?? d?.id ?? null,
    sessionId: d?.sessionId ?? d?.runId ?? null,
    status: d?.status ?? 'launched',
    summary: d?.summary ?? null,
    monthly: Array.isArray(d?.monthly) ? d.monthly : [],
    params: d?.params ?? null,
    monthsDetected: Number(d?.monthsDetected ?? 0),
    rows: Number(d?.rows ?? 0),
    cols: Number(d?.cols ?? 0),
    url: d?.url ?? null,
    format: d?.format ?? null,
  };
}

function toErr(err) {
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Request failed';
}

/* --------------------------- API ------------------------------- */

/** 1) Create a run right after Marketplace credit deduction */
export async function launchRun(payload = {}) {
  // payload: { usageLogId?, openingCash?, horizonMonths?, scenarios? }
  try {
    const { data } = await api.post('/simulator/cashflow/launch', payload);
    // -> { ok, runId, sessionId }
    return data;
  } catch (err) {
    throw new Error(toErr(err));
  }
}

/**
 * 2) Upload files (FormData)
 *    const fd = new FormData();
 *    fd.append('sessionId', runId); fd.append('runId', runId);
 *    fd.append('combined'|'sales'|'expenses', file);
 */
export async function uploadFile(formData, ids = {}) {
  if (!(formData instanceof FormData)) {
    throw new Error('uploadFile expects a FormData as the first argument');
  }

  const sid = ids.sessionId || formData.get('sessionId') || formData.get('session_id');
  const rid = ids.runId || formData.get('runId') || formData.get('run_id') || sid;

  if (sid && !formData.has('sessionId')) formData.append('sessionId', String(sid));
  if (rid && !formData.has('runId')) formData.append('runId', String(rid));
  if (sid && !formData.has('session_id')) formData.append('session_id', String(sid));
  if (rid && !formData.has('run_id')) formData.append('run_id', String(rid));

  try {
    const { data } = await api.post('/simulator/cashflow/upload', formData, {
      params: { sessionId: sid, runId: rid, session_id: sid, run_id: rid }, // compatibility
      transformRequest: [(d) => d], // don't serialize FormData
    });
    return normalizeRunPayload(data);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[uploadFile]', err?.response?.status, err?.response?.data);
    throw new Error(toErr(err));
  }
}

/** 3) Run the simulation */
export async function runSimulation(payload = {}) {
  // payload: { runId, sessionId?, openingCash?, horizonMonths?, scenarios?, seasonality? }
  try {
    const { data } = await api.post('/simulator/cashflow/run', payload);
    // -> { runId, sessionId, summary, monthly:[...] }
    return normalizeRunPayload(data);
  } catch (err) {
    throw new Error(toErr(err));
  }
}

/** 4) Fetch results (matches server routes: GET /results/:runId or /results?sessionId=) */
export async function fetchResults(arg) {
  let runId, sessionId;
  if (arg && typeof arg === 'object') { runId = arg.runId; sessionId = arg.sessionId; }
  else { runId = arg; }

  try {
    if (runId != null) {
      const { data } = await api.get(
        `/simulator/cashflow/results/${encodeURIComponent(runId)}`,
        sessionId ? { params: { sessionId } } : undefined
      );
      return normalizeRunPayload(data);
    }
    // Fallback: by session only (rare)
    const { data } = await api.get('/simulator/cashflow/results', { params: { sessionId } });
    return normalizeRunPayload(data);
  } catch (err) {
    throw new Error(toErr(err));
  }
}

/** 5) Export (XLSX or CSV). Server returns a downloadable URL */
export async function exportResults({ runId, sessionId, format = 'xlsx' }) {
  try {
    const { data } = await api.post('/simulator/cashflow/export', { runId, sessionId, format });
    // -> { runId, sessionId, url, format }
    return data;
  } catch (err) {
    throw new Error(toErr(err));
  }
}

/** 6) Optional: list recent runs */
export async function listRuns({ limit = 20 } = {}) {
  try {
    const { data } = await api.get('/simulator/cashflow/my', { params: { limit } });
    return Array.isArray(data) ? data : (data?.items || []);
  } catch (err) {
    throw new Error(toErr(err));
  }
}


/** 7) Insights (send runId; sessionId optional) */
export function getInsights({ runId, sessionId } = {}) {
  return api.post('/simulator/cashflow/insights', { runId, sessionId });
}

